import { useState } from "react";
import { generatePath, Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { ArrowLeft, ExternalLinkIcon, FileTextIcon, PencilIcon, SaveIcon } from "lucide-react";

import { cn } from "@/libs/cn";
import { useSessionStore } from "@/modules/auth/store/useSessionStore";
import { Button, DetailField, PageBreadcrumb, SelectV2, Spinner, Tag, Tooltip } from "@/modules/common/components";
import { inputClassNameValues } from "@/modules/common/components/files/Input";
import useAdminUsersOptions from "@/modules/common/hooks/lists/useAdminUsersOptions";
import useNavigateBack from "@/modules/common/hooks/useNavigateBack";
import useToastOnError from "@/modules/common/hooks/useToastOnError";
import { LEAD_STATUS_OPTIONS, LeadOriginLabels, LeadStatus } from "@/modules/common/types/Leads.enum";
import { AdminUserRole } from "@/modules/common/types/Users.enum";
import { formatDate, formatPhoneNumber } from "@/modules/common/utils/format";
import { openFileInNewTab } from "@/modules/common/utils/openFile";
import { ROUTES } from "@/routes/constants/routes";

import LeadStatusTag from "../../components/LeadStatusTag";
import useLeadDetails from "../../hooks/useLeadDetails";
import useUpdateLead from "../../hooks/useUpdateLead";

const LeadDetails = () => {
  const { id } = useParams();
  const { canUserSee } = useSessionStore();
  const canWrite = canUserSee(AdminUserRole.ADMIN);
  const navigate = useNavigate();
  const navigateBack = useNavigateBack(ROUTES.LEADS.LIST);

  const { data: lead, isLoading, isError, error } = useLeadDetails({ id: id!, enabled: !!id });
  const updateLead = useUpdateLead();

  const [statusDraft, setStatusDraft] = useState<string>();
  const [responsibleDraft, setResponsibleDraft] = useState<string | null>();
  const [observation, setObservation] = useState("");
  const [openingAttachmentId, setOpeningAttachmentId] = useState<string>();

  const responsibleOptions = useAdminUsersOptions(
    lead?.responsibleBy ? { value: lead.responsibleBy.id, label: lead.responsibleBy.name } : undefined,
  );

  useToastOnError(isError, error, "Erro ao carregar lead.", navigateBack);

  const status = statusDraft ?? lead?.status;
  const responsibleId = responsibleDraft === undefined ? lead?.responsibleBy?.id : (responsibleDraft ?? undefined);

  const hasChanges =
    !!lead &&
    ((statusDraft !== undefined && statusDraft !== lead.status) ||
      (responsibleDraft !== undefined && (responsibleDraft ?? undefined) !== lead.responsibleBy?.id) ||
      observation.trim() !== "");

  const handleViewAttachment = async (attachmentId: string, url: string) => {
    setOpeningAttachmentId(attachmentId);

    try {
      await openFileInNewTab(url);
    } catch {
      toast.error("Não foi possível abrir o arquivo. Tente baixá-lo.");
    } finally {
      setOpeningAttachmentId(undefined);
    }
  };

  const handleSave = () => {
    if (!lead || !hasChanges) return;

    updateLead.mutate(
      {
        id: lead.id,
        data: {
          status: statusDraft !== undefined && statusDraft !== lead.status ? (statusDraft as LeadStatus) : undefined,
          responsibleById:
            responsibleDraft !== undefined && (responsibleDraft ?? undefined) !== lead.responsibleBy?.id ? responsibleDraft : undefined,
          observation: observation.trim() || undefined,
        },
      },
      {
        onSuccess: () => {
          setStatusDraft(undefined);
          setResponsibleDraft(undefined);
          setObservation("");
          toast.success("Alterações salvas com sucesso!");
        },
      },
    );
  };

  return (
    <div className="flex h-full flex-col gap-4">
      <PageBreadcrumb items={[{ label: "Leads", to: ROUTES.LEADS.LIST }, ...(lead ? [{ label: lead.name }] : [])]} />

      {isLoading ? (
        <div className="flex flex-1 items-center justify-center">
          <Spinner />
        </div>
      ) : lead ? (
        <>
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex flex-col">
              <div className="flex flex-row items-center gap-2">
                <Tooltip label={lead.name}>
                  <p className="truncate text-[22px] font-bold text-ui-primary md:line-clamp-2 md:whitespace-normal">{lead.name}</p>
                </Tooltip>
                <LeadStatusTag status={lead.status} className="shrink-0" />
              </div>
              <p className="text-[14px] text-ui-text-muted">Analise a solicitação recebida e acompanhe o atendimento comercial.</p>
            </div>

            <div className="flex flex-col gap-2 sm:flex-row">
              <Button variant="outlined" label="Voltar" icon={<ArrowLeft />} className="w-full sm:w-auto" onClick={navigateBack} />
              {canWrite && (
                <Button
                  label="Editar"
                  icon={<PencilIcon />}
                  className="w-full sm:w-auto"
                  onClick={() => navigate(generatePath(ROUTES.LEADS.EDIT, { id: lead.id }))}
                />
              )}
            </div>
          </div>

          <div className="flex flex-col gap-4 rounded-xl bg-white p-4">
            <div className="flex flex-col">
              <p className="text-[16px] font-bold text-ui-primary">Dados do lead</p>
              <p className="text-[12px] text-ui-text-muted">Informações de contato da empresa.</p>
            </div>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <DetailField label="Nome">{lead.name}</DetailField>
              <DetailField label="Empresa">{lead.company}</DetailField>
              <DetailField label="E-mail">{lead.email}</DetailField>
              <DetailField label="Telefone / WhatsApp">{lead.phoneNumber ? formatPhoneNumber(lead.phoneNumber) : null}</DetailField>
              <DetailField label="Segmento">{lead.segment}</DetailField>
            </div>
          </div>

          <div className="flex flex-col gap-4 rounded-xl bg-white p-4">
            <div className="flex flex-col">
              <p className="text-[16px] font-bold text-ui-primary">Solicitação</p>
              <p className="text-[12px] text-ui-text-muted">Origem do lead e necessidade informada.</p>
            </div>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <DetailField label="Origem">
                <Tag label={LeadOriginLabels[lead.originType] ?? lead.originType} variant="primary" className="border-0 py-0" />
              </DetailField>
              <DetailField label="Data de recebimento">{formatDate(lead.createdAt, "DD/MM/YYYY [às] HH:mm")}</DetailField>
            </div>

            <DetailField label="Necessidade informada" valueClassName="items-start whitespace-pre-wrap">
              {lead.interest}
            </DetailField>
          </div>

          <div className="flex flex-col gap-4 rounded-xl bg-white p-4">
            <div className="flex flex-col">
              <p className="text-[16px] font-bold text-ui-primary">Fatura de energia</p>
              <p className="text-[12px] text-ui-text-muted">Arquivos anexados pelo lead ou pela equipe.</p>
            </div>

            {lead.attachments.length ? (
              <div className="flex flex-col gap-2">
                {lead.attachments.map((attachment) => (
                  <div
                    key={attachment.id}
                    className="flex flex-col gap-2 rounded-lg border border-ui-border p-3 sm:flex-row sm:items-center"
                  >
                    <div className="flex min-w-0 flex-1 items-center gap-2">
                      <FileTextIcon className="h-5 w-5 shrink-0 text-ui-text-muted" />
                      <span className="truncate text-sm text-ui-text">{attachment.fileName ?? "Anexo"}</span>
                    </div>

                    <div className="flex gap-2">
                      <Button
                        type="button"
                        variant="outlined"
                        label="Visualizar"
                        icon={<ExternalLinkIcon size={16} />}
                        loading={openingAttachmentId === attachment.id}
                        onClick={() => handleViewAttachment(attachment.id, attachment.url)}
                      />
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-ui-text-muted">Nenhuma fatura anexada.</p>
            )}
          </div>

          <div className="flex flex-col gap-4 rounded-xl bg-white p-4">
            <div className="flex flex-col">
              <p className="text-[16px] font-bold text-ui-primary">Acompanhamento comercial</p>
              <p className="text-[12px] text-ui-text-muted">Atualize o status, o responsável e registre observações do atendimento.</p>
            </div>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <SelectV2
                mode="single"
                label="Status"
                options={LEAD_STATUS_OPTIONS}
                value={status}
                disabled={!canWrite || updateLead.isPending}
                onChange={(next) => setStatusDraft(next)}
              />

              <SelectV2
                mode="single"
                label="Responsável"
                placeholder="Não atribuído"
                options={responsibleOptions.options}
                isLoading={responsibleOptions.isLoading}
                hasMore={responsibleOptions.hasMore}
                onLoadMore={responsibleOptions.onLoadMore}
                onSearch={responsibleOptions.onSearch}
                value={responsibleId}
                disabled={!canWrite || updateLead.isPending}
                onChange={(next) => setResponsibleDraft(next ?? null)}
              />
            </div>

            <div className="flex flex-col gap-1">
              <label className="text-sm font-semibold">Observações</label>
              <label className="text-xs text-ui-text-muted font-medium">Adicione uma nota ao histórico do lead</label>
              <textarea
                rows={3}
                value={observation}
                disabled={!canWrite || updateLead.isPending}
                onChange={(event) => setObservation(event.target.value)}
                placeholder="Registre aqui informações do atendimento..."
                className={cn(inputClassNameValues, "h-auto resize-y")}
              />
            </div>

            {canWrite && (
              <div className="flex justify-end">
                <Button
                  label="Salvar alterações"
                  icon={<SaveIcon />}
                  loading={updateLead.isPending}
                  disabled={!hasChanges || updateLead.isPending}
                  className="w-full sm:w-auto"
                  onClick={handleSave}
                />
              </div>
            )}
          </div>

          <div className="flex flex-col gap-4 rounded-xl bg-white p-4">
            <div className="flex flex-col">
              <p className="text-[16px] font-bold text-ui-primary">Histórico</p>
              <p className="text-[12px] text-ui-text-muted">Evolução do lead, da observação mais recente para a mais antiga.</p>
            </div>

            {lead.observations.length ? (
              <div className="flex flex-col">
                {lead.observations.map((item) => (
                  <div
                    key={item.id}
                    className="flex flex-col gap-1 border-t border-ui-border py-3 first:border-t-0 first:pt-0 sm:flex-row sm:gap-4"
                  >
                    <span className="shrink-0 text-sm text-ui-text-muted sm:w-40">{formatDate(item.createdAt, "DD/MM/YYYY HH:mm")}</span>
                    <div className="flex min-w-0 flex-1 flex-col">
                      <span className="whitespace-pre-wrap wrap-break-word text-sm text-ui-text-dark">{item.content}</span>
                      <span className="text-xs text-ui-text-muted">
                        {item.authorType === "SYSTEM" ? (
                          "Sistema"
                        ) : item.author ? (
                          <Link
                            to={generatePath(ROUTES.USERS.DETAILS, { id: item.author.id })}
                            className="underline cursor-pointer hover:text-ui-text"
                          >
                            {item.author.name}
                          </Link>
                        ) : (
                          "Usuário"
                        )}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-ui-text-muted">Nenhum registro no histórico ainda.</p>
            )}
          </div>
        </>
      ) : null}
    </div>
  );
};

export default LeadDetails;
